import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { PHYSICIAN_PRACTICE_FAQ } from "@/lib/faq";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Calendar, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";

// Priority FAQs — the eight highest-intent questions from Texas physicians
// and healthcare practice owners (curated from PHYSICIAN_PRACTICE_FAQ).
// Rendered above the extended list AND emitted as the first block in the
// FAQPage JSON-LD so schema and visible content align 1:1.
const PRIORITY_FAQ = PHYSICIAN_PRACTICE_FAQ.slice(0, 8).map(({ q, a }) => [q, a]);
const EXTENDED_FAQ = PHYSICIAN_PRACTICE_FAQ.slice(8).map(({ q, a }) => [q, a]);
const FAQ = PHYSICIAN_PRACTICE_FAQ.map(({ q, a }) => [q, a]);

const REGULATORY_TERMS = [
  ["Corporate practice of medicine", "Texas's rule that non-physicians cannot own a professional medical entity or employ physicians to practice medicine. The rule shapes every physician-practice transaction structure in the state."],
  ["Management-services organization (MSO)", "The buyer-owned entity that acquires the non-clinical assets and provides administrative services to the physician-owned professional entity under a long-term management-services agreement."],
  ["Stark law", "42 U.S.C. §1395nn — prohibits physicians from referring Medicare or Medicaid patients for designated health services to entities with which they have a financial relationship, absent a statutory exception."],
  ["Anti-kickback statute", "42 U.S.C. §1320a-7b — prohibits remuneration to induce federal-healthcare-program referrals. Applies to every element of a transaction with a referral component."],
  ["Fair-market-value opinion", "The written valuation opinion that supports the price, MSO fees, and lease rates against Stark and anti-kickback scrutiny."],
  ["Texas §15.50(b) — physician non-compete", "The Texas statute governing enforceability of non-competes against physicians, with specific requirements including a buy-out provision and continuity-of-care rules."],
  ["Payor credentialing", "The process by which each payor (Medicare, Medicaid, commercial insurers) recognizes the acquiring entity's tax ID and pays claims — typically 60–120 days after application."],
  ["DEA and CLIA registrations", "Facility-level registrations that require re-issuance in the buyer entity's name; timing coordination is critical to avoid controlled-substance or laboratory service gaps at closing."],
  ["Personal goodwill vs. enterprise goodwill", "For C-corp practices, the allocation of value between the physician's personal goodwill (owned individually) and the entity's enterprise goodwill can materially reduce the seller's tax result."],
  ["Section 1060 allocation", "The IRS-required allocation of purchase price across seven asset classes — negotiated in the APA and reported by both parties on Form 8594."],
];

const PROCESS_STEPS = [
  ["Discovery & Structure Conference", "We identify the physician's goals — retirement, partial exit, hospital affiliation, private-equity partnership, family transition — and select the structure that fits: full sale, MSO, partial sale, or de novo affiliation."],
  ["Regulatory Framework", "We coordinate the Stark and anti-kickback analysis, fair-market-value support, corporate-practice-of-medicine structure, and payor-credentialing pathway that will govern the transaction."],
  ["LOI & Definitive Agreement Drafting", "We prepare or negotiate the Letter of Intent, then draft the Asset Purchase Agreement (or MSO agreement package) with the healthcare-specific representations, restrictive covenants, and closing conditions the transaction requires."],
  ["Diligence, Licensing & Credentialing", "We coordinate legal, financial, tax, and regulatory diligence; prepare license and DEA transfer applications; initiate payor credentialing in the acquiring entity's name; and manage patient-notification requirements."],
  ["Employment & Restrictive Covenants", "We draft physician employment agreements with §15.50(b)-compliant non-competes, non-solicits, and compensation formulas that survive Stark and anti-kickback scrutiny."],
  ["Closing & Post-Closing Transition", "We execute the closing, coordinate the transition of records, staff, credentialing, and facility licenses, and support the buyer and seller through the 60–120-day post-closing period until payor credentialing completes."],
];

const IN_PAGE_NAV = [
  ["Structure", "structure"], ["Regulatory", "regulatory"], ["Framework", "framework"],
  ["Process", "process"], ["Coordinated Planning", "coordinated"], ["Why Lone Star Counsel", "why-lsc"],
  ["FAQs", "faqs"], ["Contact", "contact"],
];

function Bullet({ children }) {
  return (
    <li className="flex gap-3 py-3 hairline-top">
      <span className="text-brass mt-1.5 shrink-0" aria-hidden>&#9733;</span>
      <span className="text-navy font-serif leading-relaxed">{children}</span>
    </li>
  );
}

function ConsultForm() {
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    specialty: "", role: "seller",
    structure: "undecided",
    revenue: "", timeline: "",
    message: "", website: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.website) { setStatus("success"); return; }
    if (!f.name || !f.email || !f.phone) { setError("Please provide name, email, and phone."); return; }
    setStatus("loading"); setError("");
    try {
      const message = [
        `Transaction Role: ${f.role}`,
        f.specialty && `Specialty / Practice Type: ${f.specialty}`,
        `Preferred Structure: ${f.structure}`,
        f.revenue && `Annual Revenue Range: ${f.revenue}`,
        f.timeline && `Target Closing Window: ${f.timeline}`,
        `Landing Page: ${window.location.pathname}`,
        f.message && `\n${f.message}`,
      ].filter(Boolean).join("\n");
      await axios.post(`${API}/api/intake`, {
        full_name: f.name, email: f.email, phone: f.phone,
        practice_area: "Physician & Healthcare Practice Transitions",
        stage: f.role === "seller" ? "succession" : "growth",
        estimated_value: f.revenue || undefined,
        timeline: f.timeline || undefined,
        message,
        preferred_contact: "email",
        matter_class: "standard",
        role: f.role,
      });
      setStatus("success");
    } catch (err) { setError(err.response?.data?.detail || "Please try again."); setStatus("error"); }
  };
  if (status === "success") return <div className="p-8 bg-ivory border border-brass/40" data-testid="physician-form-success">
    <div className="kicker">Thank you</div>
    <h3 className="mt-3 font-display text-3xl">Message received.</h3>
    <p className="mt-3 text-slate2 font-cormorant italic">A member of the firm will be in touch within two business days.</p>
  </div>;
  return <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="physician-consult-form">
    <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
    <label className="block"><div className="field-label mb-2">Name</div><input required className="field" value={f.name} onChange={set("name")} data-testid="physician-form-name" /></label>
    <label className="block"><div className="field-label mb-2">Email</div><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="physician-form-email" /></label>
    <label className="block"><div className="field-label mb-2">Phone</div><input required type="tel" className="field" value={f.phone} onChange={set("phone")} data-testid="physician-form-phone" /></label>
    <label className="block"><div className="field-label mb-2">Buyer or Seller</div>
      <select className="field" value={f.role} onChange={set("role")} data-testid="physician-form-role">
        <option value="seller">Selling my practice</option>
        <option value="buyer">Acquiring a practice</option>
        <option value="partial">Partial sale / MSO / affiliation</option>
        <option value="succession">Internal succession / partner transition</option>
      </select>
    </label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Specialty / Practice Type</div><input className="field" value={f.specialty} onChange={set("specialty")} placeholder="e.g. Family Medicine, Cardiology, Dermatology, Dental" data-testid="physician-form-specialty" /></label>
    <label className="block"><div className="field-label mb-2">Preferred Structure</div>
      <select className="field" value={f.structure} onChange={set("structure")} data-testid="physician-form-structure">
        <option value="undecided">Not yet decided</option>
        <option value="full-sale">Full asset sale</option>
        <option value="mso">MSO transaction (private equity / hospital)</option>
        <option value="partial">Partial equity sale</option>
        <option value="succession">Internal partner buyout</option>
        <option value="affiliation">De novo affiliation</option>
      </select>
    </label>
    <label className="block"><div className="field-label mb-2">Annual Revenue Range</div>
      <select className="field" value={f.revenue} onChange={set("revenue")} data-testid="physician-form-revenue">
        <option value="">Prefer not to say</option>
        <option value="under-1M">Under $1M</option>
        <option value="1-3M">$1M – $3M</option>
        <option value="3-10M">$3M – $10M</option>
        <option value="10-25M">$10M – $25M</option>
        <option value="25M-plus">Over $25M</option>
      </select>
    </label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Target Closing Window</div>
      <select className="field" value={f.timeline} onChange={set("timeline")} data-testid="physician-form-timeline">
        <option value="">Not yet defined</option>
        <option value="under-6mo">Within 6 months</option>
        <option value="6-12mo">6 – 12 months</option>
        <option value="1-2yr">1 – 2 years</option>
        <option value="2-5yr">2 – 5 years</option>
      </select>
    </label>
    <label className="block md:col-span-2"><div className="field-label mb-2">Message</div><textarea rows={4} className="field" value={f.message} onChange={set("message")} data-testid="physician-form-message" /></label>
    {error && <div className="md:col-span-2 text-oxblood font-serif italic text-sm">— {error}</div>}
    <button type="submit" disabled={status === "loading"} className="md:col-span-2 btn-primary disabled:opacity-40" data-testid="physician-form-submit">
      {status === "loading" ? "Sending…" : "Schedule a Practice Transition Conference"}
    </button>
  </form>;
}

function FaqItem({ q, a, i, onOpen }) {
  const [open, setOpen] = useState(i === 0);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };
  return <div className="hairline-top py-6" data-testid={`physician-faq-${i}`}>
    <button onClick={toggle} aria-expanded={open} className="w-full flex justify-between items-start gap-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass">
      <h3 className="font-display text-xl md:text-2xl text-navy leading-tight">{q}</h3>
      <ChevronDown size={22} className={`shrink-0 text-brass transition-transform mt-1 ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`${open ? "block" : "hidden"} mt-4 text-slate2 font-serif text-base leading-relaxed`}>{a}</div>
  </div>;
}

export default function PhysicianPracticeFlagship() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-physician-practice-sale-attorney",
      page_title: "Texas Physician Practice Sale Attorney",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);
  return (
    <div className="bg-ivory" data-testid="physician-flagship-page">
      <SEO
        title="Texas Physician Practice Sale Attorney | Lone Star Counsel"
        description="Legal counsel for Texas physician, dental, veterinary, and healthcare practice sales, MSO transactions, and succession — Stark and anti-kickback compliant, corporate-practice-of-medicine compliant, coordinated with payor credentialing and fair-market-value opinions. Lone Star Counsel."
        keywords="Texas Physician Practice Sale Attorney, medical practice sale Texas, MSO transaction attorney Texas, physician practice acquisition Texas, dental practice sale attorney Texas, veterinary practice sale Texas, Stark law attorney Texas, anti-kickback attorney Texas, corporate practice of medicine Texas, physician non-compete Texas, healthcare M&A Texas"
        path="/texas-physician-practice-sale-attorney/"
        schema={[
          legalServiceSchema({
            name: "Texas Physician Practice Sale Attorney",
            description: "Legal counsel for Texas physician, dental, veterinary, and other healthcare practice sales, acquisitions, MSO transactions, and succession — including corporate-practice-of-medicine, Stark, anti-kickback, and payor-credentialing coordination.",
            url: "https://lonestarcounsel.com/texas-physician-practice-sale-attorney/",
          }),
          attorneySchema(),
          faqPageSchema(FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Texas Physician Practice Sale Attorney", path: "/texas-physician-practice-sale-attorney/" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="physician-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy">
              Texas Physician Practice<br/>
              <span className="italic text-oxblood">Sale Attorney.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed">
              Deliberate legal counsel for Texas physicians, dentists, veterinarians, optometrists,
              and other healthcare professionals selling, acquiring, affiliating, or planning the
              succession of a professional practice. Lone Star Counsel coordinates the transaction,
              the healthcare-regulatory framework, the tax structure, and the payor-credentialing
              transition under one attorney-CPA roof.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="physician-hero-primary">Schedule a Practice Transition Conference</a>
              <a href={`tel:${PHONE_TEL}`} className="btn-ghost" data-testid="physician-hero-secondary">
                <Phone size={16} className="inline mr-2" />Call Lone Star Counsel
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/img/practice-boardroom.jpg"
                alt="Texas physician practice sale planning session — Lone Star Counsel (illustrative)"
                className="w-full h-full object-cover"
                loading="eager" fetchPriority="high"
              />
              <div className="absolute inset-0 ring-1 ring-brass/40 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* In-page navigation */}
      <nav aria-label="On this page" className="sticky top-16 z-30 bg-ivory/95 backdrop-blur border-y border-brass/30" data-testid="physician-inpage-nav">
        <ul className="max-w-[1600px] mx-auto px-6 md:px-12 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.24em] text-navy">
          {IN_PAGE_NAV.map(([label, id]) => (
            <li key={id}><a href={`#${id}`} className="hover:text-oxblood focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass py-1" data-testid={`physician-nav-${id}`}>{label}</a></li>
          ))}
        </ul>
      </nav>

      {/* INTRO */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <p className="text-lg md:text-xl text-navy font-serif leading-relaxed">
          A Texas physician practice sale is not a standard business acquisition. The corporate-practice-of-medicine doctrine controls the ownership structure. The Stark law and the federal anti-kickback statute constrain every element of the compensation stack. Fair-market-value support is required, not optional. Payor credentialing takes months and cannot be compressed. Physician non-competes are governed by their own Texas statute. And the professional entity itself must remain physician-owned — no exception, no workaround.
        </p>
        <p className="mt-6 text-lg md:text-xl text-navy font-serif leading-relaxed">
          Lone Star Counsel represents Texas physicians, dentists, veterinarians, optometrists, chiropractors, and other licensed healthcare professionals in every phase of a practice transition: full asset sales, MSO transactions with private equity or hospital systems, partial equity sales, partner buyouts, de novo affiliations, and multi-generational family-practice succession. Led by Darryl V. Pratt, Esq., CPA, the firm brings the legal, tax, and healthcare-regulatory disciplines under a single engagement.
        </p>
        <div className="mt-12 p-8 bg-linen border-l-4 border-brass">
          <div className="kicker">Planning Today. Protecting Tomorrow.&reg;</div>
          <p className="mt-3 font-cormorant italic text-xl md:text-2xl text-navy leading-snug">
            The value of a physician's life work is decided in the ninety days before signing — not in the ninety days after.
          </p>
        </div>
      </section>

      {/* STRUCTURE */}
      <section id="structure" className="bg-linen py-16 md:py-24" data-testid="physician-structure">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-navy leading-[1.1]">Four Transaction Structures <span className="italic text-oxblood">for a Texas Practice.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">The right structure depends on who the buyer is, what the physician wants after closing, the payor mix, the referral profile, and the tax objectives of both sides. Lone Star Counsel evaluates each of the four principal structures against those variables:</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-ivory p-8 border-t-2 border-brass" data-testid="physician-structure-full">
              <div className="text-4xl font-display text-brass">01.</div>
              <h3 className="mt-4 font-display text-xl text-navy">Full Asset Sale</h3>
              <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">The buyer's professional entity (or MSO plus professional entity) acquires the non-clinical assets. The seller-physician exits after a transition period, typically 6–24 months as a W-2 employee.</p>
            </div>
            <div className="bg-ivory p-8 border-t-2 border-brass" data-testid="physician-structure-mso">
              <div className="text-4xl font-display text-brass">02.</div>
              <h3 className="mt-4 font-display text-xl text-navy">MSO Transaction</h3>
              <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">A private-equity, physician-network, or hospital-affiliated MSO acquires the non-clinical assets and manages operations. The physician-owned professional entity remains and retains clinical decisions. Often paired with physician-equity rollover into the MSO.</p>
            </div>
            <div className="bg-ivory p-8 border-t-2 border-brass" data-testid="physician-structure-partial">
              <div className="text-4xl font-display text-brass">03.</div>
              <h3 className="mt-4 font-display text-xl text-navy">Partial Sale / Equity Rollover</h3>
              <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">The physician sells a controlling interest but retains meaningful equity — a "second bite of the apple" structure common in private-equity roll-ups. Requires careful documentation of the retained equity's terms and future liquidity path.</p>
            </div>
            <div className="bg-ivory p-8 border-t-2 border-brass" data-testid="physician-structure-succession">
              <div className="text-4xl font-display text-brass">04.</div>
              <h3 className="mt-4 font-display text-xl text-navy">Internal Partner Succession</h3>
              <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">The retiring physician sells to existing physician partners — often over 5–10 years — through a coordinated buy-sell, funded by life insurance, group credit facility, or seller-financed installment. Preserves practice continuity and physician ownership.</p>
            </div>
          </div>
        </div>
      </section>

      {/* REGULATORY */}
      <section id="regulatory" className="bg-navy text-ivory py-16 md:py-24" data-testid="physician-regulatory">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker kicker-ivory">The Regulatory Envelope</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl leading-[1.02]">Six rules govern <span className="italic text-brass">every Texas physician transaction.</span></h2>
          <p className="mt-6 font-serif leading-relaxed text-ivory/90">
            Standard M&A practice cannot be layered onto a healthcare transaction without adjustment. Six regulatory disciplines — three federal, three Texas-specific — control the structure:
          </p>
          <ul className="mt-6">
            {[
              ["Corporate practice of medicine (Texas)", "Non-physicians cannot own the professional entity or employ physicians to practice medicine. Every buyer structure must respect this rule."],
              ["Stark law (federal)", "42 U.S.C. §1395nn — strict-liability prohibition on physician self-referral for designated health services with Medicare/Medicaid patients. Every financial relationship must fit an exception."],
              ["Anti-kickback statute (federal)", "42 U.S.C. §1320a-7b — intent-based prohibition on remuneration inducing federal-healthcare-program referrals. Fair-market-value documentation is essential."],
              ["Texas Business & Commerce Code §15.50(b) (physician non-competes)", "Specific Texas rules for enforceability of physician non-competes — buy-out provision, patient-list access, and continuity-of-care requirements are mandatory."],
              ["HIPAA and Texas Medical Records Privacy Act", "Patient-record transfer, business-associate agreements, and patient-notification requirements govern every practice transition."],
              ["Payor credentialing (Medicare, Medicaid, commercial)", "Not law strictly, but a critical-path operational reality — credentialing cannot begin before the acquiring entity has a tax ID, cannot conclude before closing without payor cooperation, and drives 60–120 days of post-closing cash-flow risk."],
            ].map(([t, d], i) => (
              <li key={i} className="flex gap-3 py-4 border-t border-brass/25">
                <span className="text-brass mt-1.5 shrink-0" aria-hidden>&#9733;</span>
                <div>
                  <div className="font-display text-brass text-sm uppercase tracking-widest">{t}</div>
                  <p className="mt-1 font-serif leading-relaxed text-ivory/85">{d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FRAMEWORK */}
      <section id="framework" className="bg-ivory py-16 md:py-24" data-testid="physician-framework">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">What the Transaction Should Address</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Ten instruments that carry a <span className="italic text-oxblood">Texas physician sale.</span></h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">A complete Texas physician-practice transaction is a coordinated document set — not a single agreement. The document family typically includes:</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="physician-framework-list">
            {REGULATORY_TERMS.map(([title, desc], i) => (
              <div key={i} className="py-4 hairline-top" data-testid={`physician-term-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-brass" aria-hidden>&#9733;</span>
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-slate2 leading-relaxed font-serif">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-linen py-16 md:py-24" data-testid="physician-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Our Process</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Six steps, <span className="italic text-oxblood">measured against the credentialing clock.</span></h2>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="physician-process-list">
            {PROCESS_STEPS.map(([t, d], i) => (
              <li key={i} className="bg-ivory p-8 border-t-2 border-brass" data-testid={`physician-step-${i}`}>
                <div className="text-4xl font-display text-brass">{String(i + 1).padStart(2, "0")}.</div>
                <h3 className="mt-4 font-display text-xl text-navy">{t}</h3>
                <p className="mt-3 text-sm text-slate2 font-serif leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ATTORNEY-CPA */}
      <section className="bg-ivory py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Attorney &middot; CPA &middot; Business Advisor</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">The numbers behind the <span className="italic text-oxblood">medical practice.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">Physician-practice transactions sit at the intersection of business law, healthcare regulation, tax planning, valuation, insurance, and estate planning. As a dually licensed Texas attorney and Certified Public Accountant, Darryl V. Pratt is positioned to coordinate the legal terms, the tax structure, and the financial modeling in a single engagement — while working alongside the client's independent CPA, healthcare-regulatory specialist, and financial advisor.</p>
        </div>
      </section>

      {/* COORDINATED */}
      <section id="coordinated" className="bg-linen py-16 md:py-24" data-testid="physician-coordinated">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Coordinated Planning</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">A Practice Sale Never Stands <span className="italic text-oxblood">Alone.</span></h2>
          <p className="mt-6 text-navy font-serif leading-relaxed">The physician's practice is usually the largest asset in the retirement plan. The transaction must be coordinated with the surrounding disciplines so the sale, the tax treatment, the retirement structure, and the estate plan describe the same event.</p>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.24em]">
            <Link to="/texas-asset-purchase-agreement-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Asset Purchase Agreements</Link>
            <Link to="/texas-business-succession-planning-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Business Succession Planning</Link>
            <Link to="/texas-trademark-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Trademark &amp; Brand Protection</Link>
            <Link to="/practice" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Business Counsel</Link>
            <Link to="/practice/private-client" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Private Client Services</Link>
            <Link to="/texas-estate-planning-attorney/" className="text-oxblood border-b border-oxblood/50 hover:border-oxblood pb-0.5">Estate Planning</Link>
          </div>
        </div>
      </section>

      {/* WHY LSC */}
      <section id="why-lsc" className="bg-ivory py-16 md:py-24" data-testid="physician-why">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Why Choose Us</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Why Choose <span className="italic text-oxblood">Lone Star Counsel?</span></h2>
          <ul className="mt-8">
            <Bullet>Nearly 30 years of combined legal and financial experience from a Texas attorney and CPA.</Bullet>
            <Bullet>Focused counsel for Texas physician-practice sales, MSO transactions, partner successions, and healthcare M&amp;A — with corporate-practice-of-medicine, Stark, and anti-kickback structuring under one engagement.</Bullet>
            <Bullet>Deliberate representation of Texas physicians, dentists, veterinarians, optometrists, chiropractors, and multi-specialty groups.</Bullet>
            <Bullet>Coordinated planning that treats the practice sale as a life-decision — not just a transaction — with the tax, retirement, and estate integration the physician's family deserves.</Bullet>
            <Bullet>Attorney-CPA perspective that matters most at the moment goodwill is allocated, receivables are valued, and the earn-out formula is negotiated.</Bullet>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="bg-linen py-16 md:py-24" data-testid="physician-faq">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked Questions</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Answers <span className="italic text-oxblood">before the transition.</span></h2>
          <p className="mt-4 max-w-3xl text-navy/75 font-serif leading-relaxed">
            The most common questions we receive from Texas physicians, dentists, veterinarians, and healthcare practice owners, answered directly. General information only &mdash; not legal, tax, or medical advice on your specific matter.
          </p>
          <div className="mt-10" data-testid="physician-priority-faq">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <FaqItem
                key={`priority-${i}`}
                q={q}
                a={a}
                i={`priority-${i}`}
                onOpen={() => trackPriorityFaqOpen({
                  page_slug: "texas-physician-practice-sale-attorney",
                  faq_position: i + 1,
                  faq_question: q,
                })}
              />
            ))}
          </div>
          <div className="mt-16 pt-10 border-t border-brass/30" data-testid="physician-extended-faq">
            <div className="kicker">Deep-dive answers</div>
            <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-tight">
              Extended reference &mdash; {EXTENDED_FAQ.length} additional questions from Texas healthcare owners.
            </h3>
            <div className="mt-6">
              {EXTENDED_FAQ.map(([q, a], i) => <FaqItem key={i} q={q} a={a} i={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* PLAN THE NEXT CHAPTER — contextual cross-links */}
      <section id="physician-next-chapter" className="bg-ivory py-16 md:py-24 border-t border-brass/25" data-testid="physician-next-chapter">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related counsel</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.05]">
            The transaction is one step. <span className="italic text-oxblood">The plan is the arc.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            A physician-practice sale is rarely the end of the plan. Lone Star Counsel coordinates the transaction with the practice's business succession, the physician's estate plan, the trust architecture that holds the proceeds, and the brand and asset planning that surrounds the practice.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-testid="physician-next-chapter-links">
            <Link to="/texas-asset-purchase-agreement-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="physician-next-apa">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Asset Purchase Agreement</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                The transaction template for non-healthcare business acquisitions and MSO carve-outs.
              </h3>
            </Link>
            <Link to="/texas-business-succession-planning-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="physician-next-succession">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Business Succession</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Partner successions, retirement transitions, and internal buyouts inside the practice.
              </h3>
            </Link>
            <Link to="/trusts/business-owner-trust-planning" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="physician-next-trust">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Business-Owner Trust</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Trust ownership of the practice's non-licensed assets and the coordinated estate plan.
              </h3>
            </Link>
            <Link to="/texas-trademark-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="physician-next-trademark">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Trademark &amp; Brand</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Protecting the practice name, logo, and any signature service programs.
              </h3>
            </Link>
            <Link to="/texas-estate-planning-attorney/" className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3" data-testid="physician-next-estate">
              <div className="flex items-baseline gap-3">
                <Star size={9} className="text-brass fill-brass" aria-hidden />
                <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">Estate Planning</span>
              </div>
              <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                Wills, revocable trusts, and family-wealth coordination once the practice is sold.
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-ivory py-16 md:py-24" data-testid="physician-contact">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="kicker">Talk With a Texas Physician Practice Sale Attorney</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.02]">Schedule a Practice Transition <span className="italic text-oxblood">Conference.</span></h2>
            <p className="mt-6 text-navy font-serif leading-relaxed">Whether you are five years from retirement, considering a private-equity or hospital affiliation, planning an internal partner succession, or acquiring a practice, Lone Star Counsel can help you plan and execute the transaction that fits your practice, your family, and your timeline.</p>
            <div className="mt-8 p-6 bg-linen border-l-4 border-brass">
              <div className="kicker">Contact</div>
              <p className="mt-3 font-serif text-navy">
                <a href={`tel:${PHONE_TEL}`} className="text-oxblood font-display text-2xl">{PHONE}</a><br/>
                <span className="text-slate2 italic font-cormorant">2591 Dallas Parkway, Suite 300, Frisco, Texas 75034</span><br/>
                <span className="text-sm text-slate2">Serving physicians, dentists, veterinarians, and healthcare professionals throughout Texas.</span>
              </p>
            </div>
          </div>
          <div><ConsultForm /></div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-navy text-ivory/70 py-10 text-xs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-serif leading-relaxed">
          <strong className="text-brass tracking-widest uppercase text-[10px]">Disclaimer.</strong> The information on this page is for general informational purposes only and is not legal, tax, accounting, financial, or medical advice. Reading this page or contacting the firm does not create an attorney-client relationship. An attorney-client relationship is established only through a written agreement signed by the client and the firm. Past results do not guarantee future outcomes. Each physician-practice transaction depends on its specific facts and applicable law, including federal healthcare-regulatory rules and Texas-specific requirements.
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-navy text-ivory border-t border-brass flex" data-testid="physician-sticky-cta">
        <a href={`tel:${PHONE_TEL}`} className="flex-1 py-4 text-center border-r border-brass/40 uppercase tracking-widest text-xs" data-testid="physician-sticky-call">
          <Phone size={14} className="inline mr-1" /> Call
        </a>
        <a href="#contact" className="flex-1 py-4 text-center bg-brass text-navy uppercase tracking-widest text-xs font-semibold" data-testid="physician-sticky-schedule">
          <Calendar size={14} className="inline mr-1" /> Schedule
        </a>
      </div>
    </div>
  );
}
